import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Calendar, FileText, Github, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { serviceImage } from '@/components/services/ServiceCard';
import { detailProjectIds } from './ProjectDetail';

import { CALENDLY_URL } from '@/lib/site';

interface Project {
  id: string;
  title: string;
  description: string;
  category: string;
  image: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
  pdfUrl?: string;
  featured: boolean;
  fullDescription?: string;
  features?: string[];
  challenges?: string[];
  solutions?: string[];
  screenshots?: string[];
  client?: string;
  duration?: string;
  year?: string;
}

const projects: Project[] = [
  {
    id: 'google-ads-kogit',
    title: 'Google Ads Management — Kogit Campaign',
    description: 'Built and ran the full Google Ads + organic strategy for Kogit Labs. 6.9K active users, 25K page views, reach across 7 countries.',
    category: 'Marketing',
    image: '/kogitstatschart.png',
    technologies: ['Google Ads', 'PPC', 'Search Campaigns', 'Conversion Tracking', 'GA4'],
    featured: true,
    client: 'Kogit Labs',
    duration: 'Ongoing',
    year: '2024'
  },
  {
    id: 'hrflare',
    title: 'HR FLARE — AI HR Platform',
    description: 'A complete HR platform for small and growing teams: core HR, recruiting, payroll, and an AI copilot scoped to each user\'s permissions.',
    category: 'Software',
    image: '/products/hrflare-live-dashboard.webp',
    technologies: ['Angular', 'Supabase', 'TypeScript', 'MCP', 'OAuth 2.1', 'Tailwind CSS'],
    liveUrl: 'https://hrflare.com/',
    featured: true,
    fullDescription: 'HR FLARE is a full HR information system built for small and growing teams. It brings employees, documents, hiring and payroll into one workspace, with an AI copilot that answers from live HR data and executes real actions — always limited to what the signed-in user is allowed to see. It speaks the Model Context Protocol, so teams can manage HR directly from Claude or ChatGPT.',
    features: [
      'AI copilot answering from live HR data, scoped per role',
      'MCP integration with OAuth 2.1 — connect Claude or ChatGPT, no API keys',
      'Recruiting pipeline with public job pages and AI resume parsing',
      'Payroll with multi-currency, deductions and payment history',
      'Attendance, leave, org chart, documents and e-signature',
      'Capacity planning from GitHub issues and Google Calendar',
      'Multi-company tenancy — several companies under one login',
      '50+ granular permissions across 4 core roles',
      'GDPR-compliant, bilingual English and German',
    ],
    client: 'OmniCraft Services (in-house product)',
    year: '2026',
    screenshots: ['/products/hrflare-live-dashboard.webp', '/products/hrflare-live-ai.webp', '/products/hrflare-live-core-hr.webp']
  },
  {
    id: 'learnkogit',
    title: 'Learn Kogit',
    description: 'Educational platform providing comprehensive learning resources for students and professionals.',
    category: 'Web Development',
    image: '/kogit.png',
    technologies: ['React', 'Node.js', 'Supabase', 'Express'],
    liveUrl: 'https://www.learnkogit.com/',
    featured: true,
    fullDescription: 'LearnKogit is an interactive learning portal designed to help users enhance their knowledge through structured video-based courses, quizzes, and personal progress tracking.',
    features: [
      'Authentication with Supabase (email/password, Google OAuth, session management)',
      'Course Dashboard with real-time progress tracking and video player',
      'User Dashboard with personalized progress and certificates',
      'Admin Panel for content management and analytics',
      'Google Analytics integration for custom event tracking',
      'Scalable deployment on GCP with CI/CD',
    ],
    client: 'Learn Kogit Inc. (USA, New York)',
    duration: '4 months',
    year: '2023',
    screenshots: ['/kogit.png']
  },
  {
    id: 'ledgertree',
    title: 'Ledger Tree Financial Group',
    description: 'Professional website for a financial services firm with client portal integration.',
    category: 'Web Development',
    image: '/ledgertree.png',
    technologies: ['WordPress', 'Custom PHP', 'Financial APIs', 'Responsive Design'],
    liveUrl: 'https://ledgertreefinancialgroup.com/',
    featured: true,
    fullDescription: 'Ledger Tree Financial Group needed a professional website that reflected their expertise and trustworthiness in the financial sector. The project involved creating a modern, responsive website with detailed service pages, team profiles, and a secure client portal for document sharing and financial updates.',
    features: [
      'Custom WordPress theme development',
      'Responsive design with modern aesthetics',
      'Secure client portal with document sharing',
      'Blog with financial insights and updates',
      'Team profiles and service details',
      'Contact forms with filtering by department',
      'Full website SEO optimization',
      'Google Analytics integration for custom event tracking',
      'Scalable deployment on GCP with CI/CD',
    ],
    client: 'Ledger Tree Financial Group',
    duration: '3 month',
    year: '2025',
    screenshots: ['/ledgertree.png']
  },
  {
    id: 'italian-food-logo',
    title: 'Italian Food Company Logo',
    description: 'Modern and elegant logo design for an authentic Italian food company.',
    category: 'Creative Design',
    image: '/italianlogo.png',
    technologies: ['Logo Design', 'Branding', 'Adobe Illustrator', 'Typography'],
    pdfUrl: 'https://www.behance.net/gallery/226104911/Logo-Design',
    featured: true
  },
  {
    id: 'ledgertree-branding',
    title: 'Ledger Tree Financial Branding',
    description: 'Comprehensive brand identity for a financial services group.',
    category: 'Creative Design',
    image: '/residencylogo.png',
    technologies: ['Corporate Branding', 'Financial Sector Design', 'Brand Guidelines', 'Marketing Materials'],
    pdfUrl: 'https://www.behance.net/gallery/226104071/Logo-Design-Ledger-Tree-Financial-Group',
    featured: true
  },
  {
    id: 'ai-chatbot',
    title: 'Customer Service AI Chatbot',
    description: 'AI-powered chatbot designed to handle customer inquiries and provide instant support.',
    category: 'AI Solutions',
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5',
    technologies: ['OpenAI API', 'Python', 'Natural Language Processing', 'React'],
    liveUrl: 'https://www.chatbotstudy.app/login',
    featured: false
  },
  {
    id: 'ecommerce',
    title: 'Petstore E-commerce Platform',
    description: 'Fully-featured online store with product management, checkout, and payment processing.',
    category: 'Web Development',
    image: '/ecommerce.png',
    technologies: ['Javascript', 'opensource Theme', 'Payment Gateway Integration'],
    liveUrl: 'https://codewithsadee.github.io/kitter/',
    featured: false
  },
  {
    id: 'arch-residence',
    title: 'Modern Residential Design',
    description: 'Architectural design for a contemporary family home with sustainable features.',
    category: 'Architecture',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c',
    technologies: ['3D Modeling', 'AutoCAD', 'Sustainable Design', 'Interior Planning'],
    liveUrl: 'https://www.behance.net/gallery/191908015/BArch-Portfolio',
    featured: false
  },
  {
    id: 'brand-identity',
    title: 'Christmas Light Brand Identity',
    description: 'Complete brand identity design including logo, color palette, and marketing materials.',
    category: 'Creative Design',
    image: '/chrismis.png',
    technologies: ['Adobe Creative Suite', 'Brand Strategy', 'Logo Design', 'Typography'],
    liveUrl: 'https://www.behance.net/gallery/226103253/Logo-Design-CNY-Christmas-Lights',
    featured: false
  },
  {
    id: 'Blog Template',
    title: 'MOLO News | Blog Template',
    description: "Feature-rich, clean blog template for writers and storytellers.",
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://molo.news/',
    featured: false
  },
  {
    id: 'dental-template',
    title: 'Dental Services Website',
    description: "Professional template for dentists and dental clinics.",
    category: 'Web Development',
    image: '/dental.png',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://codewithsadee.github.io/dentelo/',
    featured: false
  },
  {
    id: 'realestate-example',
    title: 'Real Estate Website Example',
    description: 'A stunning real estate platform for agencies and independent agents.',
    category: 'Web Development',
    image: '/realestatewebsite.png',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://codewithsadee.github.io/homeverse/',
    featured: false
  },
  {
    id: 'product-template',
    title: 'Learning Platform landing Page',
    description: "Landing page template for learning platform.",
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://kogit.pages.dev/',
    featured: false
  },
  {
    id: 'startup-template',
    title: 'Startup Website Landing Page',
    description: "Modern and responsive landing page template for startups.",
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://startup-website-temp.pages.dev/',
    featured: false
  },
  {
    id: 'tourism-template',
    title: 'Tourism Agency',
    description: "Template for tourism, travel agencies, and destination discovery sites.",
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb',
    technologies: ['HTML', 'CSS', 'JavaScript'],
    liveUrl: 'https://codewithsadee.github.io/tourest/',
    featured: false
  }
];

const categories = ['All', 'Software', 'Web Development', 'Creative Design', 'AI Solutions', 'Marketing', 'Architecture'];

// Shown large at the top of the unfiltered view, in this order.
const featuredIds = ['hrflare', 'learnkogit', 'google-ads-kogit', 'ledgertree'];

const highlights: Record<string, string> = {
  learnkogit: '1,500+ users',
  'google-ads-kogit': '6.9K active users',
  hrflare: 'Our own product',
};

// Check if a project is a logo project by checking if it has a pdfUrl and contains "Logo" in the title
const isLogoProject = (project: Project) => project.pdfUrl && project.title.includes('Logo');

const hasDetails = (p: Project) =>
  Boolean(p.fullDescription || p.features?.length || p.challenges?.length || p.solutions?.length || p.screenshots?.length || p.client || p.duration || p.year);

type Action = { label: string; href: string; external?: boolean; icon?: React.ReactNode };

/** The links a project card offers, most important first. */
const projectActions = (p: Project): Action[] => {
  const actions: Action[] = [];
  if (p.id === 'learnkogit' || p.id === 'google-ads-kogit') {
    actions.push({ label: p.id === 'learnkogit' ? 'Success story' : 'Case study', href: '/case-study/kogit', icon: <TrendingUp size={15} /> });
  }
  if (p.id === 'google-ads-kogit') actions.push({ label: 'View service', href: '/google-ads-management' });
  if (p.id === 'hrflare') actions.push({ label: 'Product page', href: '/products' });
  if (isLogoProject(p) && p.pdfUrl) {
    actions.push({ label: 'View on Behance', href: p.pdfUrl, external: true });
  } else if (detailProjectIds.has(p.id) && hasDetails(p)) {
    actions.push({ label: 'Details', href: `/portfolio/${p.id}` });
  }
  if (p.liveUrl) actions.push({ label: 'Live site', href: p.liveUrl, external: true });
  return actions;
};

const ActionLink = ({ action, primary }: { action: Action; primary: boolean }) => {
  const className = cn(
    'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
    primary
      ? 'bg-primary text-white hover:bg-primary/90'
      : 'border border-slate-200 text-slate-700 hover:border-primary/40 hover:text-primary dark:border-white/10 dark:text-slate-300'
  );
  const content = (
    <>
      {action.icon}
      {action.label}
      {action.external ? <ArrowUpRight size={14} /> : !action.icon && <ArrowRight size={14} />}
    </>
  );
  return action.external ? (
    <a href={action.href} target="_blank" rel="noopener noreferrer" className={className}>{content}</a>
  ) : (
    <Link to={action.href} className={className}>{content}</Link>
  );
};

const ProjectCard = ({ project, size = 'default' }: { project: Project; size?: 'default' | 'large' }) => {
  const actions = projectActions(project);
  const highlight = highlights[project.id];
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white/80 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 dark:border-white/10 dark:bg-white/[0.03]">
      <div className={cn('relative overflow-hidden bg-slate-100 dark:bg-slate-900', size === 'large' ? 'aspect-[16/9]' : 'aspect-[16/10]')}>
        <img
          src={serviceImage(project.image, size === 'large' ? 1400 : 800)}
          alt={project.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80" />
        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
          <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-900 backdrop-blur dark:bg-slate-950/70 dark:text-white">
            {project.category}
          </span>
          {highlight && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-medium text-white">
              <TrendingUp size={12} /> {highlight}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className={cn('font-semibold tracking-tight text-slate-900 dark:text-white', size === 'large' ? 'text-2xl' : 'text-lg')}>
            {project.title}
          </h3>
          {project.year && <span className="shrink-0 font-mono text-xs text-slate-400">{project.year}</span>}
        </div>
        {project.client && <p className="mt-1 text-sm text-primary">{project.client}</p>}
        <p className={cn('mt-3 leading-relaxed text-slate-600 dark:text-slate-400', size === 'large' ? 'text-base line-clamp-3' : 'text-sm line-clamp-2')}>
          {project.description}
        </p>

        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Technologies">
          {project.technologies.slice(0, size === 'large' ? 6 : 4).map(tech => (
            <li key={tech} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600 dark:border-white/10 dark:text-slate-300">
              {tech}
            </li>
          ))}
        </ul>

        {(actions.length > 0 || project.githubUrl || (project.pdfUrl && !isLogoProject(project))) && (
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-6">
            {actions.map((action, i) => <ActionLink key={action.label} action={action} primary={i === 0} />)}
            {project.githubUrl && (
              <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" aria-label="GitHub repository" className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white">
                <Github size={16} />
              </a>
            )}
            {project.pdfUrl && !isLogoProject(project) && (
              <a href={project.pdfUrl} target="_blank" rel="noopener noreferrer" aria-label="View PDF" className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white">
                <FileText size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const counts = useMemo(() => {
    const c: Record<string, number> = { All: projects.length };
    projects.forEach(p => { c[p.category] = (c[p.category] || 0) + 1; });
    return c;
  }, []);

  const featured = featuredIds.map(id => projects.find(p => p.id === id)).filter((p): p is Project => Boolean(p));

  // Unfiltered: featured projects sit in their own section, so the grid shows the rest.
  const gridProjects = useMemo(
    () => activeFilter === 'All'
      ? projects.filter(p => !featuredIds.includes(p.id))
      : projects.filter(p => p.category === activeFilter),
    [activeFilter]
  );

  return (
    <>
      <Helmet>
        <title>Portfolio | Omnicraft Services - Our Projects & Case Studies</title>
        <meta name="description" content="Explore Omnicraft Services' portfolio of web development, branding, AI, and creative projects. See our work and client success stories." />
        <link rel="icon" type="image/png" href="/logo.png" />
        <link rel="apple-touch-icon" href="/logo.png" />
      </Helmet>

      <div className="relative isolate min-h-screen overflow-hidden pt-16">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px]">
          <div className="absolute left-1/2 top-[-180px] h-[520px] w-[820px] max-w-full -translate-x-1/2 rounded-full bg-primary/25 blur-[120px]" />
          <div className="absolute left-[-120px] top-48 h-72 w-72 rounded-full bg-emerald-500/15 blur-[100px]" />
        </div>

        {/* Hero */}
        <section className="container-custom pb-12 pt-14 md:pt-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Selected work
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight [text-wrap:balance] md:text-6xl">
              Products, platforms and brands{' '}
              <span className="bg-gradient-to-r from-primary via-sky-400 to-emerald-400 bg-clip-text text-transparent">we have shipped.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              From our own AI HR platform to client websites, campaigns and brand identities. Each project represents our commitment to quality, innovation and client satisfaction.
            </p>
          </div>
        </section>

        {/* Featured */}
        {activeFilter === 'All' && featured.length > 0 && (
          <section className="container-custom pb-16">
            <h2 className="mb-6 text-sm font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Featured work</h2>
            <div className="grid gap-6 lg:grid-cols-2">
              {featured.map(project => (
                <ProjectCard key={project.id} project={project} size="large" />
              ))}
            </div>
          </section>
        )}

        {/* Filters + grid */}
        <section className="container-custom pb-20">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h2 className="text-3xl font-bold tracking-tight">{activeFilter === 'All' ? 'More projects' : activeFilter}</h2>
            <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div role="tablist" aria-label="Filter projects" className="flex w-max gap-2">
                {categories.filter(c => counts[c]).map(category => {
                  const active = activeFilter === category;
                  return (
                    <button
                      key={category}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveFilter(category)}
                      className={cn(
                        'inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        active
                          ? 'border-primary bg-primary text-white shadow-lg shadow-primary/25'
                          : 'border-slate-200 bg-white/70 text-slate-700 hover:border-primary/40 hover:text-primary dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300'
                      )}
                    >
                      {category}
                      <span className={cn('rounded-full px-1.5 text-xs tabular-nums', active ? 'bg-white/20' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400')}>
                        {counts[category]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {gridProjects.map(project => <ProjectCard key={project.id} project={project} />)}
          </div>
        </section>

        {/* CTA */}
        <section className="container-custom pb-20">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary via-blue-600 to-violet-600 px-6 py-12 text-white shadow-2xl shadow-primary/20 md:px-12">
            <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h2 className="text-3xl font-bold tracking-tight">Have a project in mind?</h2>
                <p className="mt-2 text-white/85">Tell us what you want to build. We will reply with an honest plan and a price.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" variant="secondary" className="rounded-full">
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Calendar className="mr-2 h-4 w-4" /> Book a call
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-primary">
                  <Link to="/contact">Contact us <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Portfolio;
